import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, UserPlus, ClipboardList, ShieldCheck, LogIn } from "lucide-react";

function Space({ id, eyebrow, title, text, points, img, alt, reverse, cta }) {
  return (
    <div id={id} className={`grid lg:grid-cols-12 gap-8 lg:gap-14 items-center scroll-mt-20 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <div className="lg:col-span-5">
        <div className="text-xs font-bold text-[#1F7A63] uppercase tracking-[0.18em]">{eyebrow}</div>
        <h3 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{title}</h3>
        <p className="mt-3 text-[#5C736A] leading-relaxed">{text}</p>
        <ul className="mt-6 space-y-3">
          {points.map((p) => (
            <li key={p} className="flex items-start gap-3 text-sm"><span className="mt-0.5 w-5 h-5 rounded-full bg-[#E8F2EF] grid place-items-center shrink-0"><Check className="w-3 h-3 text-[#1F7A63]" /></span>{p}</li>
          ))}
        </ul>
        <Link to={cta.to} className="inline-block mt-7"><Button className="h-12 px-6 bg-[#1F7A63] hover:bg-[#165c4b] text-white font-semibold">{cta.label} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
      </div>
      <div className="lg:col-span-7">
        <div className="rounded-2xl overflow-hidden border border-[#1A2E28]/10 shadow-xl bg-white">
          <img src={img} alt={alt} className="w-full h-auto block" loading="lazy" />
        </div>
      </div>
    </div>
  );
}

export function Spaces() {
  return (
    <section className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20 md:space-y-28">
        <Space id="directeur" eyebrow="Espace Directeur" title="Pilotez tout votre établissement"
          text="Le directeur dispose d'une vue d'ensemble : effectifs, enseignants, examens, présences et finances de l'école."
          points={["Effectifs, garçons et filles, classes en un coup d'œil", "Création des comptes enseignants et affectation des classes", "Inscriptions, mensualités, cotisations, dépenses et impayés", "Résultats, classements et bulletins PDF de toute l'école", "Suivi des heures des enseignants (cantum horaire)"]}
          img="/screens/directeur.jpg" alt="Capture de l'espace Directeur EDUKATI" cta={{ to: "/inscription?profil=directeur", label: "Inscrire mon école" }} />
        <Space id="enseignant" reverse eyebrow="Espace Enseignant" title="Votre classe, simplement"
          text="L'enseignant gère une ou plusieurs classes, y compris en multigrade ou en double flux, depuis son téléphone."
          points={["Appel quotidien, retards et taux de présence", "Saisie des notes, moyennes, rangs et bulletins", "Comportements, félicitations, sanctions et difficultés", "Indice de performance et évolution de la classe", "Espace personnel : documents et documentation de l'école"]}
          img="/screens/enseignant.jpg" alt="Capture de l'espace Enseignant EDUKATI" cta={{ to: "/inscription?profil=maitre", label: "Créer mon compte enseignant" }} />
      </div>
    </section>
  );
}

const STEPS = [
  { icon: UserPlus, title: "Choisissez votre profil", text: "Directeur d'établissement ou enseignant." },
  { icon: ClipboardList, title: "Remplissez votre formulaire", text: "Quelques minutes suffisent, même sur téléphone." },
  { icon: ShieldCheck, title: "Attendez la validation", text: "L'administrateur EDUKATI examine votre demande." },
  { icon: LogIn, title: "Connectez-vous", text: "Et commencez à gérer votre école ou vos classes." },
];

export function HowItWorks() {
  return (
    <section id="comment" className="py-16 md:py-24 bg-[#1A2E28] text-white scroll-mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs font-bold text-[#E6B800] uppercase tracking-[0.18em]">Comment ça marche</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: "Outfit" }}>Comment fonctionne EDUKATI ?</h2>
        </div>
        <ol className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map(({ icon: Icon, title, text }, i) => (
            <li key={title} className="relative rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition-colors" data-testid={`how-step-${i + 1}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-[#E6B800]">Étape {i + 1}</span>
                <Icon className="w-5 h-5 text-white/70" />
              </div>
              <div className="mt-6 text-lg font-bold text-white" style={{ fontFamily: "Outfit" }}>{title}</div>
              <p className="mt-1.5 text-sm text-white/70">{text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
